import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// Salva il preventivo (con link per riprenderlo) e porta alla pagina del preventivo salvato.
export const SalvaPreventivoDialog = ({ open, onOpenChange, codice, product, input, ente: enteCfg }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ nome: "", cognome: "", email: "", telefono: "", consenso_privacy: false });
  const [e, setE] = useState({ ragione_sociale: "", codice_fiscale: "", tipo_ente: "ETS", runts: "iscritto", ruolo: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (ev) => setF({ ...f, [k]: ev.target.value });
  const setEnte = (k) => (ev) => setE({ ...e, [k]: ev.target.value });
  const enteOk = !enteCfg || (e.ragione_sociale && e.codice_fiscale);

  const salva = async () => {
    setBusy(true);
    try {
      const { consenso_privacy, ...cliente } = f;
      const { data } = await api.post("/public/accordi/preventivi", { codice, product, input, cliente, consenso_privacy, origin: window.location.origin, ...(enteCfg ? { ente: e } : {}) });
      toast.success("Preventivo salvato: ti abbiamo inviato il link per riprenderlo");
      navigate(`/accordi/preventivo/${data.token}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="salva-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">Salva il preventivo</DialogTitle>
          <DialogDescription>Ricevi via email il link per riprenderlo in qualsiasi momento e procedere quando vuoi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {enteCfg && (
            <div className="space-y-3 rounded-xl border bg-sage/30 p-3" data-testid="salva-ente">
              <div className="text-xs font-semibold uppercase tracking-wide text-forest">Ente contraente</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5"><Label>Denominazione dell'ente *</Label><Input value={e.ragione_sociale} onChange={setEnte("ragione_sociale")} data-testid="salva-ente-nome" /></div>
                <div className="space-y-1.5"><Label>Codice fiscale ente *</Label><Input value={e.codice_fiscale} onChange={(ev) => setE({ ...e, codice_fiscale: ev.target.value.toUpperCase() })} data-testid="salva-ente-cf" /></div>
                <div className="space-y-1.5"><Label>Tipo di ente</Label><select value={e.tipo_ente} onChange={setEnte("tipo_ente")} data-testid="salva-ente-tipo" className="h-10 w-full rounded-md border bg-white px-2 text-sm">{enteCfg.tipi_ente.map((t) => <option key={t}>{t}</option>)}</select></div>
                <div className="space-y-1.5"><Label>RUNTS</Label><select value={e.runts} onChange={setEnte("runts")} data-testid="salva-ente-runts" className="h-10 w-full rounded-md border bg-white px-2 text-sm">{enteCfg.runts.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div>
                <div className="space-y-1.5"><Label>Il tuo ruolo nell'ente</Label><Input value={e.ruolo} onChange={setEnte("ruolo")} placeholder="es. Presidente" data-testid="salva-ente-ruolo" /></div>
              </div>
              <div className="text-xs text-muted-foreground">Sotto: i dati della persona di riferimento dell'ente.</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome *</Label><Input value={f.nome} onChange={set("nome")} data-testid="salva-nome" /></div>
            <div className="space-y-1.5"><Label>Cognome *</Label><Input value={f.cognome} onChange={set("cognome")} data-testid="salva-cognome" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="salva-email" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={f.telefono} onChange={set("telefono")} data-testid="salva-telefono" /></div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <Checkbox checked={f.consenso_privacy} onCheckedChange={(v) => setF({ ...f, consenso_privacy: !!v })} data-testid="salva-privacy" />
            Acconsento al trattamento dei dati per la gestione del preventivo (Reg. UE 2016/679).
          </label>
          <Button onClick={salva} disabled={busy || !f.nome || !f.cognome || !f.email || !f.consenso_privacy || !enteOk} data-testid="salva-conferma" className="w-full bg-forest hover:bg-forest-hover">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva preventivo
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
